import BookingAboutToExpireDialog from '@/components/purchase/checkout/expiration-timer/BookingAboutToExpireDialog';
import BookingExpiredDialog from '@/components/purchase/checkout/expiration-timer/BookingExpiredDialog';
import {
  BookingExpiryContext,
  type BookingExpiryContextValue,
} from '@/contexts/useBookingExpiry';
import {
  abandonExchange,
  resetPurchase,
} from '@/features/purchase/purchaseActions';
import { useExpiryCountdown } from '@/hooks/useExpiryCountdown';
import { useInProgressBookingController } from '@/hooks/useInProgressBooking';
import { useSearchWidget } from '@/hooks/useSearchWidget';
import { useStartedExchangeOperation } from '@/hooks/useStartedExchangeOperation';
import { persistor } from '@/store';
import { useDispatch } from '@/store/utils';
import { formatCountdown } from '@/utils/dateTime';
import {
  JOURNEY_CHANGE_ROUTE,
  journeyChangePath,
} from '@/utils/journeyChangeUrlParams';
import type React from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

interface BookingExpiryProviderProps {
  children: React.ReactNode;
}

export function BookingExpiryProvider({
  children,
}: BookingExpiryProviderProps): React.ReactElement {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setCurrentSearchFormValues } = useSearchWidget();
  const {
    bookingId,
    booking,
    isValidating,
    countdown: inProgressCountdown,
    handleCheckoutClick,
  } = useInProgressBookingController();
  const [expiringDismissed, setExpiringDismissed] = useState(false);

  // The exchange operation we started this session (only set after
  // `startExchange` — i.e. the change-booking checkout step, never during
  // ChangeTrips selection). When present we honour its `confirmableUntil`
  // (the window to pay for the change); otherwise it's a regular purchase
  // and we use the purchase booking's booking-level limit.
  const activeExchangeOperation = useStartedExchangeOperation();
  const confirmableUntil = activeExchangeOperation
    ? activeExchangeOperation.confirmableUntil
    : booking?.confirmableUntil;
  const countdown = useExpiryCountdown(confirmableUntil);

  // Calculate expiry flags
  const isExpiring =
    countdown !== null && countdown.minutes < 5 && countdown.minutes >= 0;
  const isExpired =
    countdown !== null && countdown.minutes === 0 && countdown.seconds === 0;

  // Only show modals once validation settles AND there's something with a
  // real expiry: a purchase booking, or (change-booking) the started op.
  const canShowModals =
    !isValidating && (!!bookingId || !!activeExchangeOperation);

  // Only show expired modal if countdown is initialized (not null) and expired
  // Null countdown means no booking data yet, so we won't show the modal
  const showExpiredDialog = canShowModals && isExpired;

  // Expiring dialog can show on hydration (legitimate warning for active bookings)
  const showExpiringDialog =
    canShowModals && isExpiring && !expiringDismissed && !isExpired;

  // Reset dismiss state when the tracked entity changes (purchase booking
  // or the active exchange operation).
  const activeId = bookingId ?? activeExchangeOperation?.id;
  const previousActiveIdRef = useRef(activeId);
  if (activeId !== previousActiveIdRef.current) {
    previousActiveIdRef.current = activeId;
    setExpiringDismissed(false);
  }

  const dismissExpiringWarning = useCallback(() => {
    setExpiringDismissed(true);
  }, []);

  const handleBookingExpired = useCallback(() => {
    if (activeExchangeOperation?.id) {
      // Change-booking expired: abandon the lapsed exchange operation (cancel
      // + reset) and return the user to where the change started — for the
      // journey-change deep-link flow that's its own selection step, since it
      // has no manage-booking view to fall back to.
      dispatch(abandonExchange());
      setCurrentSearchFormValues(undefined);
      persistor.purge();
      const { pathname, search } = window.location;
      navigate(
        pathname.startsWith(JOURNEY_CHANGE_ROUTE)
          ? journeyChangePath(search)
          : '/manage-booking/details'
      );
      return;
    }

    dispatch(resetPurchase());
    setCurrentSearchFormValues(undefined);
    persistor.purge();
    navigate('/purchase');
  }, [
    activeExchangeOperation?.id,
    dispatch,
    navigate,
    setCurrentSearchFormValues,
  ]);

  const formattedCountdown = formatCountdown(countdown);

  const contextValue = useMemo<BookingExpiryContextValue>(
    () => ({
      countdown,
      isExpiring,
      isExpired,
      dismissExpiringWarning,
      bookingId,
      isValidating,
      inProgressCountdown,
      handleCheckoutClick,
    }),
    [
      countdown,
      isExpiring,
      isExpired,
      dismissExpiringWarning,
      bookingId,
      isValidating,
      inProgressCountdown,
      handleCheckoutClick,
    ]
  );

  return (
    <BookingExpiryContext.Provider value={contextValue}>
      {children}

      {/* Global expiry modals */}
      <BookingAboutToExpireDialog
        open={showExpiringDialog}
        timeTillExpiry={formattedCountdown}
        setOpen={(open) => {
          if (!open) dismissExpiringWarning();
        }}
        onCancel={dismissExpiringWarning}
      />
      <BookingExpiredDialog
        open={showExpiredDialog}
        setOpen={() => {}}
        onCancel={handleBookingExpired}
        isChangeBooking={!!activeExchangeOperation}
      />
    </BookingExpiryContext.Provider>
  );
}
